import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { UI, LANG } from "../data/content";
import { ContactModule, usePageMeta } from "../components/kit";

const el = LANG === "el";
const loc = el ? "el-GR" : "en-GB";
const fmtD = (d) => (d ? new Date(d).toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric" }) : "—");
const chip = (active) => `topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${active ? "!bg-[var(--accent)] !text-white" : ""}`;

export const FundingCalendar = () => {
  const C = UI.fcal;
  usePageMeta(`${C.title} | KairosHK`, C.lead);
  const [calls, setCalls] = useState([]);
  const [country, setCountry] = useState(null);
  const [flat, setFlat] = useState(false);
  useEffect(() => { api.get("/funding/calendar").then(({ data }) => setCalls(data)).catch(() => {}); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const filtered = calls.filter((c) => !country || c.country === country);
  const groups = [];
  filtered.forEach((c) => {
    const key = (c.closes_at || "9999-99").slice(0, 7);
    const g = groups.find((x) => x.key === key);
    if (g) g.items.push(c); else groups.push({ key, items: [c] });
  });
  const icsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/funding/calendar.ics${country ? `?country=${country}` : ""}`;
  const Row = ({ c }) => {
    const closed = c.closes_at && c.closes_at < today;
    return (
      <div className={`flex flex-wrap items-baseline gap-x-6 gap-y-1 py-4 border-b border-line ${closed ? "opacity-45" : ""}`} data-testid={`fcal-row-${c.fcall_id}`}>
        <span className="text-brand font-semibold w-32 whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtD(c.closes_at)}</span>
        <div className="flex-1 min-w-[240px]">
          <p className="font-medium">{el ? c.title_el : c.title_en}{c.estimated && <span className="text-[var(--accent)] text-xs font-semibold ml-2 uppercase">{C.estimated}</span>}</p>
          <p className="text-xs text-ink-muted mt-0.5">{el ? c.programme_name_el : c.programme_name_en} · {c.status}{closed ? ` · ${C.closed}` : ""} · {C.verified} {c.verified_at}</p>
        </div>
        <span className="topic-tag">{c.country}</span>
        {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-brand text-sm underline">{C.source}</a>}
      </div>
    );
  };
  return (
    <div data-testid="funding-calendar-page">
      <section className="k-section !pb-8">
        <div className="k-container">
          <p className="eyebrow mb-4">{UI.funding.eyebrow}</p>
          <h1 className="k-h1 mb-5">{C.title}</h1>
          <p className="standfirst max-w-3xl mb-8">{C.lead}</p>
          <div className="flex flex-wrap gap-2 mb-6">
            <button onClick={() => setCountry(null)} className={chip(!country)} data-testid="fcal-filter-all">{C.all}</button>
            {["GR", "CY", "EU"].map((c) => <button key={c} onClick={() => setCountry(country === c ? null : c)} className={chip(country === c)} data-testid={`fcal-filter-${c}`}>{c}</button>)}
            <button onClick={() => setFlat(!flat)} className="btn-secondary !py-2 !px-4 text-xs ml-auto" data-testid="fcal-view-toggle">{flat ? C.monthView : C.listView}</button>
          </div>
          <div className="border border-line bg-paper-alt p-5 mb-10 flex flex-wrap items-center gap-4" data-testid="ics-subscribe-block">
            <p className="text-sm flex-1 min-w-[260px]"><span className="font-semibold">{C.icsTitle}</span> — {C.icsBody}</p>
            <a href={icsUrl} className="btn-primary !py-2 !px-5 text-xs" data-testid="ics-subscribe-btn">{C.icsBtn}</a>
          </div>
          {flat ? (
            <div className="border-t border-line" data-testid="fcal-list">{filtered.map((c) => <Row key={c.fcall_id} c={c} />)}</div>
          ) : groups.map(({ key, items }) => (
            <div key={key} className="mb-10" data-testid={`fcal-month-${key}`}>
              <h2 className="text-base font-semibold capitalize border-b-2 border-[var(--accent)] inline-block pb-2">{fmtD(`${key}-01`).replace(/^1 /, "").replace(/^1η /, "")}</h2>
              <div className="border-t border-line">{items.map((c) => <Row key={c.fcall_id} c={c} />)}</div>
            </div>
          ))}
          <p className="text-sm border border-line bg-paper-alt p-5 mt-8 max-w-3xl" data-testid="fcal-handoff">{UI.funding.handoff}</p>
        </div>
      </section>
    </div>
  );
};

export const Cashflow = () => {
  const C = UI.cashflow;
  usePageMeta(`${C.title} | KairosHK`, C.lead);
  const [f, setF] = useState({ value: "500000", intensity: "50", duration: "18", advance: "25", lag: "6", vat: "24", vat_lag: "4", claims: "2" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const { data } = await api.post("/funding/cashflow", { ...f });
    setRes(data);
  };
  const dl = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/funding/cashflow.xlsx", { ...f, lang: LANG }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "kairoshk-cashflow.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };
  const eur = (n) => `€${Number(n).toLocaleString(loc)}`;
  const Field = ({ label, k }) => (
    <div className="flex flex-col"><label className="field-label flex-1">{label}</label>
      <input type="number" className="field-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cf-${k}`} /></div>
  );
  const maxAbs = res ? Math.max(...res.rows.map((r) => Math.abs(r.cumulative)), 1) : 1;
  return (
    <div data-testid="cashflow-page">
      <section className="k-section !pb-8">
        <div className="k-container">
          <p className="eyebrow mb-4">{UI.funding.eyebrow}</p>
          <h1 className="k-h1 mb-5">{C.title}</h1>
          <p className="standfirst max-w-3xl mb-10">{C.lead}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mb-8" data-testid="cashflow-form">
            <Field label={C.qValue} k="value" />
            <Field label={C.qIntensity} k="intensity" />
            <Field label={C.qDuration} k="duration" />
            <Field label={C.qAdvance} k="advance" />
            <Field label={C.qLag} k="lag" />
            <Field label={C.qVat} k="vat" />
            <Field label={C.qVatLag} k="vat_lag" />
            <Field label={C.qClaims} k="claims" />
          </div>
          <button onClick={run} className="btn-primary" data-testid="cashflow-run-btn">{C.run}</button>
        </div>
      </section>
      {res && (
        <section className="k-section !py-12 bg-paper-alt" data-testid="cashflow-results">
          <div className="k-container">
            <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mb-12">
              <div className="border-t-2 border-[var(--accent)] pt-4" data-testid="cf-peak-gap">
                <p className="text-3xl font-bold text-brand" style={{ fontVariantNumeric: "tabular-nums" }}>{eur(res.peak_gap)}</p>
                <p className="text-sm text-ink-muted mt-1">{C.peakGap} · {C.peakMonth} {res.peak_month}</p>
              </div>
              <div className="border-t-2 border-line pt-4">
                <p className="text-3xl font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>{eur(res.grant)}</p>
                <p className="text-sm text-ink-muted mt-1">{C.grant}</p>
              </div>
              <div className="border-t-2 border-line pt-4">
                <p className="text-3xl font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>{eur(res.advance_in)}</p>
                <p className="text-sm text-ink-muted mt-1">{C.advance}</p>
              </div>
            </div>
            <h2 className="k-h3 mb-4">{C.chartTitle}</h2>
            <div className="flex items-end gap-[2px] h-44 border-b border-line mb-10 max-w-5xl" data-testid="cf-chart">
              {res.rows.map((r) => (
                <div key={r.month} className="flex-1 flex flex-col justify-end h-full" title={`M${r.month}: ${eur(r.cumulative)}`}>
                  <div style={{ height: `${(Math.abs(r.cumulative) / maxAbs) * 100}%`, background: r.cumulative < 0 ? "var(--accent)" : "var(--brand)", minHeight: r.cumulative !== 0 ? 2 : 0 }} />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h2 className="k-h3">{C.tableTitle}</h2>
              <button onClick={dl} disabled={busy} className="btn-secondary !py-2 !px-5 text-xs" data-testid="cf-xlsx-btn">{C.xlsx}</button>
            </div>
            <div className="max-w-5xl overflow-x-auto">
              <table className="k-table" data-testid="cf-table">
                <thead><tr>{[C.month, C.spend, C.vatOut, C.grantIn, C.vatBack, C.net, C.cumulative].map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {res.rows.map((r) => (
                    <tr key={r.month} className={r.month === res.peak_month ? "bg-[var(--accent-soft)]" : ""}>
                      <td>{r.month}</td><td>{eur(r.spend)}</td><td>{eur(r.vat_out)}</td>
                      <td>{eur(r.grant_in)}</td><td>{eur(r.vat_back)}</td><td>{eur(r.net)}</td>
                      <td className={r.cumulative < 0 ? "text-[var(--accent)] font-semibold" : "font-semibold"}>{eur(r.cumulative)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm border border-line bg-paper p-5 mt-8 max-w-4xl" data-testid="cf-assumptions">{C.assumptions}</p>
            <p className="text-sm border border-line bg-paper p-5 mt-4 max-w-4xl" data-testid="cf-handoff">{UI.funding.handoff}</p>
          </div>
        </section>
      )}
      <section className="k-container py-16"><ContactModule /></section>
    </div>
  );
};

export const Matcher = () => {
  const M = UI.matcher;
  usePageMeta(`${M.title} | KairosHK`, M.lead);
  const [regions, setRegions] = useState([]);
  const [f, setF] = useState({ country: "GR", region: "", size: "small", sector: "manufacturing", entity: "existing", project_type: "investment", value: "", de_minimis: "" });
  const [res, setRes] = useState(null);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/funding/regions?country=GR").then(({ data }) => setRegions(data)).catch(() => {}); }, []);
  const run = async () => {
    const { data } = await api.post("/funding/match", { ...f, value: f.value || 0, de_minimis: f.de_minimis || 0 });
    setRes(data);
    setSent(false);
  };
  const sendReport = async () => {
    try {
      await api.post("/funding/match-report", { email, lang: LANG, inputs: f, matched: res.matched });
      setSent(true);
      toast.success(M.sentToast);
    } catch { toast.error("Error"); }
  };
  const Field = ({ label, children }) => (<div className="flex flex-col"><label className="field-label flex-1">{label}</label>{children}</div>);
  const sel = (key, opts) => (
    <select className="field-input" value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`m-${key}`}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div data-testid="matcher-page">
      <section className="k-section !pb-8">
        <div className="k-container">
          <p className="eyebrow mb-4">{UI.funding.eyebrow}</p>
          <h1 className="k-h1 mb-5">{M.title}</h1>
          <p className="standfirst max-w-3xl mb-10">{M.lead}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mb-8" data-testid="matcher-form">
            <Field label={M.qCountry}>{sel("country", [["GR", M.gr], ["CY", M.cy]])}</Field>
            {f.country === "GR" && (
              <Field label={M.qRegion}>{sel("region", [["", "—"], ...regions.map((r) => [r.nuts_code, el ? r.name_el : r.name_en])])}</Field>
            )}
            <Field label={M.qSize}>{sel("size", Object.entries(M.sizeOpts))}</Field>
            <Field label={M.qSector}>{sel("sector", Object.entries(M.sectorOpts))}</Field>
            <Field label={M.qEntity}>{sel("entity", Object.entries(M.entityOpts))}</Field>
            <Field label={M.qType}>{sel("project_type", Object.entries(M.typeOpts))}</Field>
            <Field label={M.qValue}><input type="number" className="field-input" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder="500000" data-testid="m-value" /></Field>
            <Field label={M.qDeMinimis}><input type="number" className="field-input" value={f.de_minimis} onChange={(e) => setF({ ...f, de_minimis: e.target.value })} placeholder="0" data-testid="m-deminimis" /></Field>
          </div>
          <button onClick={run} className="btn-primary" data-testid="matcher-run-btn">{M.run}</button>
        </div>
      </section>
      {res && (
        <section className="k-section !py-12 bg-paper-alt" data-testid="matcher-results">
          <div className="k-container max-w-4xl !mx-0">
            <h2 className="k-h2 mb-2">{M.results} ({res.matched.length})</h2>
            <div className="section-rule mb-8" />
            {res.matched.map((m) => (
              <div key={m.slug} className="border border-line bg-paper p-6 mb-5" data-testid={`match-${m.slug}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <h3 className="k-h3">{el ? m.name_el : m.name_en}</h3>
                  <span className="text-2xl font-semibold text-brand" style={{ fontVariantNumeric: "tabular-nums" }}>{m.intensity || "—"}</span>
                </div>
                <p className="text-xs text-ink-muted mb-3">
                  {m.next_deadline ? `${M.nextDeadline}: ${fmtD(m.next_deadline)}${m.next_deadline_estimated ? ` (${UI.fcal.estimated.toLowerCase()})` : ""}` : M.noDeadline}
                  {m.co_financing && (el ? m.co_financing.el : m.co_financing.en) ? ` · ${el ? m.co_financing.el : m.co_financing.en}` : ""}
                </p>
                <ul className="text-sm text-ink-muted space-y-1.5 list-none">
                  {m.reasons.map((r, i) => <li key={i} className="border-l-2 border-[var(--accent)] pl-3">{el ? r.el : r.en}</li>)}
                </ul>
              </div>
            ))}
            {res.disqualified.length > 0 && (
              <>
                <h3 className="k-h3 mt-10 mb-4">{M.notMatched}</h3>
                {res.disqualified.map((d) => (
                  <p key={d.slug} className="text-sm text-ink-muted mb-2" data-testid={`nomatch-${d.slug}`}>
                    <span className="font-semibold text-ink">{el ? d.name_el : d.name_en}</span> — {el ? d.why.el : d.why.en}
                  </p>
                ))}
              </>
            )}
            <p className="text-sm border border-line bg-paper p-5 mt-10" data-testid="matcher-handoff">{M.handoff}</p>
            <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="matcher-email-block">
              {sent ? <p className="text-sm font-semibold text-brand">{M.sentToast}</p> : (
                <>
                  <p className="text-sm w-full">{M.emailAsk}</p>
                  <input className="field-input max-w-xs" placeholder={UI.common.emailPh} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="matcher-email-input" />
                  <button onClick={sendReport} disabled={!email.includes("@")} className="btn-secondary !py-2.5 !px-5 text-xs disabled:opacity-40" data-testid="matcher-email-btn">{M.emailBtn}</button>
                </>
              )}
            </div>
          </div>
        </section>
      )}
      <section className="k-container py-16"><ContactModule /></section>
    </div>
  );
};
